import React, { useEffect, useState, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    Eye,
    X,
    CheckCircle,
    User,
    ChevronDown,
    ChevronUp,
    Paperclip,
    Send,
    Reply,
    Archive,
    CornerUpRight,
    Edit,
    ExternalLink,
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { useDispatch, useSelector } from "react-redux";
import {
    createSupportTicketNotes,
    getUserSupportTicketNotes,
    useAppDispatch,
    createChat,
    UpdateUserChats,
    createConversation,
    getUserChats,
    getUserConversations,
    updateUserTickets,
} from "../../redux";
import { uploadContractFile } from "../../redux/Slices/contractSlice";
import { hideLoader, showLoader } from "../../redux/Slices/loadingSlice";
import Spinner from "../../modules/shared/components/Spinner";
import { ContextEnum, RoleEnum, TicketStatusesEnum } from "../../modules/shared/model";
import { useWebSocket } from "../../hooks/useWebSocket";
import { createUserNotificationAction } from "../../redux/Actions/UserNotificationActions";
import { NotificationStatusEnum, NotificationTypeEnum, NotificationPriorityEnum } from "../../modules/shared/model";

interface Attachment {
    id: string;
    name: string;
    size: string;
    url?: string;
    type?: string;
}

interface Message {
    id: number;
    sender: string;
    role: string;
    message: string;
    timestamp: string;
    attachments: Attachment[];
}

const TicketDetailsModal = ({
    ticket,
    isOpen,
    onClose,
    priorityConfig,
    getPriorityName,
    getStatusName,
    statusConfig,
    getCategoryName,
    onTicketUpdate,
}: any) => {
    const { t } = useTranslation();
    const { user, token, chats, conversations } = useSelector((state: any) => state.auth);
    const { supportTicketNotes } = useSelector((state: any) => state.admin);
    const [response, setResponse] = useState("");
    const [internalNote, setInternalNote] = useState("");
    const [showHistory, setShowHistory] = useState(false);
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [fileInputKey, setFileInputKey] = useState(Date.now());
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const dispatch = useAppDispatch();
    
    // WebSocket message handler
    const handleWebSocketMessage = useCallback((message: any) => {
        // Handle both message types for compatibility
        if ((message.type === "new_support_message" || message.type === "new_conversation") && 
            message.detail?.ticketId === ticket?.id) {
            // Refresh conversations to get the latest messages
            if (chats?.id) {
                dispatch(getUserConversations({ id: chats.id })).catch((err) => {
                    console.error("Failed to refresh conversations:", err);
                });
            }
        }
    }, [ticket?.id, chats?.id, dispatch]);

    // WebSocket for real-time messaging
    const { isConnected, sendMessage: sendWebSocketMessage } = useWebSocket({
        onMessage: handleWebSocketMessage
    });
    // Map conversations to messages format
    useEffect(() => {
        if (conversations && conversations.length > 0) {
            const processedMessages = conversations.map((conv: any) => {
                const isAdmin = conv.sender?.id === user?.id;
                const senderName = isAdmin ? `${user?.firstName} ${user?.lastName}` : `${ticket?.user?.name}`;

                const mappedAttachments =
                    conv.attachments?.map((att: any) => {
                        const extension = att.path.split(".").pop()?.toLowerCase();
                        const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(extension || "");

                        return {
                            id: att.id,
                            name: att.path.split("/").pop() || "attachment",
                            size: "Unknown",
                            url: att.path,
                            type: isImage ? "image" : "file",
                        };
                    }) || [];

                return {
                    id: conv.id,
                    sender: senderName,
                    role: isAdmin ? getRoleName(RoleEnum.admin) : getRoleName(RoleEnum.client),
                    message:
                        conv.message ||
                        (mappedAttachments.length > 0 ? t("ticketDetailsModal.sentAttachments", { count: mappedAttachments.length }) : ""),
                    timestamp: conv.createdAt,
                    attachments: mappedAttachments,
                };
            });

            setMessages(
                processedMessages.sort(
                    (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                )
            );
        } else {
            // Fallback to ticket responses if no conversations exist
            setMessages(
                ticket?.responses?.map((res: any) => ({
                    id: res.id,
                    sender: res.type === "admin" ? res.author : ticket.user.name,
                    role: res.type === "admin" ? getRoleName(RoleEnum.admin) : getRoleName(RoleEnum.client),
                    message: res.content,
                    timestamp: res.timestamp,
                    attachments: res.attachments || [],
                })) || []
            );
        }
    }, [conversations, ticket, user]);

    const addNote = async () => {
        if (!internalNote.trim()) return;

        const payload = {
            ticketId: {
                id: ticket?.id,
            },
            userId: {
                id: user?.id,
            },
            note: internalNote,
        };

        await dispatch(createSupportTicketNotes({ data: payload }));
        setInternalNote("");
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setIsUploading(true);

        try {
            const newAttachments: Attachment[] = [];

            for (const file of Array.from(files)) {
                const { payload } = await dispatch(uploadContractFile(file) as any);
                const fileId = payload?.file?.id;
                const fileUrl = payload?.file?.path;

                if (fileId) {
                    newAttachments.push({
                        id: fileId,
                        name: file.name,
                        size: `${(file.size / 1024).toFixed(1)} KB`,
                        type: file.type,
                        url: fileUrl || URL.createObjectURL(file),
                    });
                }
            }

            setAttachments(prev => [...prev, ...newAttachments]);
        } finally {
            setIsUploading(false);
            setFileInputKey(Date.now());
        }
    };

    const handleRemoveAttachment = (id: string) => {
        setAttachments(prev => prev.filter(attachment => attachment.id !== id));
    };

    const onSendMessage = async () => {
        if (response.trim() || attachments.length > 0) {
            try {
                dispatch(showLoader());
                let chatResponse;

                // First create or update the chat
                const chatPayload = {
                    unreadCount: {},
                    participants: [{ id: user?.id }],
                    contextId: ticket.id,
                    contextType: { id: ContextEnum.Support_Ticket },
                    lastMessageAt: new Date().toISOString(),
                    lastMessage: response || `Sent ${attachments.length} attachment(s)`,
                };

                if (chats != null) {
                    chatResponse = await dispatch(UpdateUserChats({ id: chats?.id, data: chatPayload })).unwrap();
                } else {
                    chatResponse = await dispatch(createChat({ data: chatPayload })).unwrap();
                }

                // Then create the conversation with attachments
                const conversationPayload = {
                    isRead: false,
                    message: response,
                    sender: { id: user?.id },
                    chatId: { id: chatResponse.id },
                    attachments: attachments.map(att => ({ id: att.id })),
                };

                const conversation = await dispatch(createConversation({ data: conversationPayload })).unwrap();

                // Immediately refresh conversations to show the new message
                await dispatch(getUserConversations({ id: chatResponse.id }));

                // Send WebSocket notification to the ticket creator (non-blocking)
                setTimeout(async () => {
                    try {
                        const ticketUserId = ticket?.user?.id;
                        
                        if (sendWebSocketMessage && isConnected && ticketUserId) {
                            const messageData = {
                                receivers: [String(ticketUserId)],
                                sender: user?.id || "",
                                type: "new_support_message",
                                detail: {
                                    conversationId: conversation.id,
                                    conversation: conversation,
                                    chatId: chatResponse.id,
                                    message: conversationPayload.message || `Sent ${attachments.length} attachment(s)`,
                                    senderId: user?.id,
                                    ticketId: ticket.id,
                                    attachments: conversationPayload.attachments,
                                    timestamp: new Date().toISOString()
                                },
                                conversationId: chatResponse.id
                            };
                            
                            await sendWebSocketMessage(messageData);
                        }
                        
                        // Create in-app notification for the ticket creator with translation support
                        if (ticketUserId) {
                            await createUserNotificationAction({
                                user: { id: String(ticketUserId) },
                                titleKey: "notifications.supportTicket.newResponseTitle",
                                messageKey: "notifications.supportTicket.newResponseMessage",
                                messageParams: {
                                    ticketId: ticket.id,
                                    message: response || t("ticketDetailsModal.adminSentAttachment")
                                },
                                title: "New Response from Support", // Fallback English
                                message: response || "Admin sent an attachment",
                                url: "/support-tickets",
                                type: { id: NotificationTypeEnum["In-App"] },
                                status: { id: NotificationStatusEnum.Unread },
                                priority: { id: NotificationPriorityEnum.High },
                                expiryTime: null
                            });
                        }
                    } catch (wsError) {
                        console.error("WebSocket/Notification error:", wsError);
                    }
                }, 100);

                setResponse("");
                setAttachments([]);
            } catch (error) {
                console.error("Error sending message:", error);
            } finally {
                dispatch(hideLoader());
            }
        }
    };

    const formatTimestamp = (timestamp: string) => {
        return new Date(timestamp).toLocaleString();
    };

    const getRoleName = (roleId: number) => {
        return RoleEnum[roleId] || "Support_Ticket";
    };

    const handleUpdateTicketStatus = async (newStatus: number) => {
        try {
            dispatch(showLoader());
            
            const payload = {
                status: { id: newStatus },
            };

            await dispatch(updateUserTickets({ id: ticket.id, data: payload }));
            
            if (onTicketUpdate) {
                await onTicketUpdate();
            }
            
            dispatch(hideLoader());
            onClose();
        } catch (error) {
            console.error("Failed to update ticket status:", error);
            dispatch(hideLoader());
        }
    };

    const handleResolveTicket = () => {
        handleUpdateTicketStatus(TicketStatusesEnum.Resolved);
    };

    const handleEscalateTicket = () => {
        handleUpdateTicketStatus(TicketStatusesEnum.Escalate);
    };

    const handleArchiveTicket = () => {
        handleUpdateTicketStatus(TicketStatusesEnum.Archived);
    };

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    if (!ticket) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.95 }}
                        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                        style={{ textAlign: "left" }}>
                        <div className="p-6 border-b sticky top-0 bg-white" style={{ textAlign: "left" }}>
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="flex items-center gap-3">
                                        <h2 className="text-xl font-semibold">{t("ticketDetailsModal.ticketNumber", { id: ticket.id })}</h2>
                                        {priorityConfig[Number(ticket.priority)] && (
                                            <Badge variant={undefined} className={priorityConfig[Number(ticket.priority)].color}>
                                                {priorityConfig[Number(ticket.priority)].icon}
                                                <span className="ml-1 capitalize">
                                                    {" "}
                                                    {getPriorityName(Number(ticket.priority))} Priority
                                                </span>
                                            </Badge>
                                        )}
                                        {statusConfig[Number(ticket.status)] && (
                                            <Badge variant={undefined} className={statusConfig[Number(ticket.status)].color}>
                                                {getStatusName(Number(ticket.status))}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">
                                        {t("ticketDetailsModal.created", { date: new Date(ticket.createdAt).toLocaleString() })}
                                    </p>
                                </div>
                                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div className="flex divide-x">
                            {/* Main Content */}
                            <div className="flex-1 p-6">
                                {/* Ticket Information */}
                                <div className="space-y-6">
                                    {/* Subject and Category */}
                                    <div>
                                        <h3 className="text-lg font-semibold">{ticket.subject}</h3>
                                        <div className="flex items-center gap-2 mt-2">
                                            <Badge variant="outline">{getCategoryName(Number(ticket.category))}</Badge>
                                            <Badge variant="outline">{ticket.subcategory}</Badge>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <p className="text-gray-700">{ticket.description}</p>
                                        {ticket.attachments.length > 0 && (
                                            <div className="mt-4">
                                                <div className="text-sm font-medium text-gray-500 mb-2">
                                                    {t("ticketDetailsModal.attachments")}:
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {ticket.attachments.map((attachment: any) => (
                                                        <div
                                                            key={attachment.id}
                                                            className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border">
                                                            <Paperclip className="h-4 w-4 text-gray-400" />
                                                            <span className="text-sm">{attachment.name}</span>
                                                            <span className="text-xs text-gray-500">
                                                                ({attachment.size})
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Conversation Thread */}
                                    <div className="space-y-4">
                                        <h3 className="font-medium">{t("ticketDetailsModal.conversation")}</h3>
                                        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                                            {messages.map(message => (
                                                <div
                                                    key={message.id}
                                                    className={`flex gap-4 ${
                                                        message.role === getRoleName(RoleEnum.admin)
                                                            ? "flex-row-reverse"
                                                            : ""
                                                    }`}>
                                                    <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                        <User className="h-4 w-4 text-gray-600" />
                                                    </div>
                                                    <div
                                                        className={`flex-1 space-y-2 ${
                                                            message.role === getRoleName(RoleEnum.admin)
                                                                ? "text-right"
                                                                : ""
                                                        }`}>
                                                        <div className="inline-block max-w-[80%] bg-gray-50 rounded-lg p-4">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="font-medium">{message.sender}</span>
                                                                <span className="text-sm text-gray-500">
                                                                    {formatTimestamp(message.timestamp)}
                                                                </span>
                                                            </div>
                                                            <p className="text-gray-700">{message.message}</p>
                                                            {message.attachments.length > 0 && (
                                                                <div className="mt-3 space-y-2">
                                                                    {message.attachments.map((attachment, index) => (
                                                                        <div
                                                                            key={index}
                                                                            className="flex flex-col gap-2">
                                                                            {attachment.type === "image" &&
                                                                                attachment.url && (
                                                                                    <div className="max-w-xs rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                                                                                        <img
                                                                                            src={attachment.url}
                                                                                            alt={attachment.name}
                                                                                            className="w-full h-auto max-h-60 object-contain cursor-pointer"
                                                                                            onClick={() =>
                                                                                                window.open(
                                                                                                    attachment.url,
                                                                                                    "_blank"
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                            <div
                                                                                className={`flex items-center gap-2 text-sm p-2 rounded ${
                                                                                    attachment.type === "image"
                                                                                        ? "bg-gray-50 mt-1"
                                                                                        : "bg-white"
                                                                                }`}>
                                                                                <Paperclip className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                                                <span className="truncate">
                                                                                    {attachment.name}
                                                                                </span>
                                                                                <span className="text-gray-400 text-xs whitespace-nowrap">
                                                                                    ({attachment.size})
                                                                                </span>
                                                                                <Button
                                                                                    variant="ghost"
                                                                                    size="sm"
                                                                                    className="ml-auto p-1"
                                                                                    onClick={e => {
                                                                                        e.stopPropagation();
                                                                                        window.open(
                                                                                            attachment.url,
                                                                                            "_blank"
                                                                                        );
                                                                                    }}>
                                                                                    <ExternalLink className="w-4 h-4" />
                                                                                </Button>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div ref={messagesEndRef} />
                                        </div>

                                        {/* Response Input */}
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <div className="space-y-4">
                                                {/* Attachment Preview */}
                                                {attachments.length > 0 && (
                                                    <div className="space-y-2">
                                                        {attachments.map(attachment => (
                                                            <div
                                                                key={attachment.id}
                                                                className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                                                                <Paperclip className="w-4 h-4 text-gray-400" />
                                                                <span className="flex-1">{attachment.name}</span>
                                                                <span className="text-gray-400">
                                                                    ({attachment.size})
                                                                </span>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        handleRemoveAttachment(attachment.id)
                                                                    }>
                                                                    <X className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                <textarea
                                                    value={response}
                                                    onChange={e => setResponse(e.target.value)}
                                                    placeholder={t("ticketDetailsModal.typeYourResponse")}
                                                    rows={4}
                                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                                />
                                                <div className="flex justify-between items-center">
                                                    <div className="relative">
                                                        <Button variant="outline" size="sm">
                                                            {isUploading ? (
                                                                <>
                                                                    <Spinner size={4} />
                                                                    {t("ticketDetailsModal.uploading")}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Paperclip className="h-4 w-4 mr-2" />
                                                                    {t("ticketDetailsModal.attachFiles")}
                                                                </>
                                                            )}
                                                        </Button>
                                                        <input
                                                            key={fileInputKey}
                                                            type="file"
                                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                            multiple
                                                            onChange={handleFileUpload}
                                                            disabled={isUploading}
                                                        />
                                                    </div>
                                                    <Button
                                                        disabled={!response.trim() && attachments.length === 0}
                                                        onClick={onSendMessage}>
                                                        <Send className="h-4 w-4 mr-2" />
                                                        {t("ticketDetailsModal.sendResponse")}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sidebar */}
                            <div className="w-80 p-6 space-y-6">
                                {/* User Information */}
                                <div className="space-y-4">
                                    <h3 className="font-medium">{t("ticketDetailsModal.userInformation")}</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <span className="text-sm text-gray-500">{t("ticketDetailsModal.name")}</span>
                                            <p className="font-medium">{ticket.user.name}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-500">{t("ticketDetailsModal.email")}</span>
                                            <p className="font-medium">{ticket.user.email}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-500">{t("ticketDetailsModal.phone")}</span>
                                            <p className="font-medium">{ticket.user.phone}</p>
                                        </div>
                                        <div>
                                            <span className="text-sm text-gray-500">{t("ticketDetailsModal.userType")}</span>
                                            <Badge variant={undefined} className="mt-1">
                                                {ticket.user.type}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>

                                {/* Assignment */}
                                <div className="space-y-4">
                                    <h3 className="font-medium">{t("ticketDetailsModal.assignment")}</h3>
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{ticket.assignedTo.name}</p>
                                                <p className="text-sm text-gray-500">{ticket.assignedTo.email}</p>
                                            </div>
                                            <Button variant="outline" size="sm">
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {/* Internal Notes */}
                                <div className="space-y-4">
                                    <h3 className="font-medium">{t("ticketDetailsModal.internalNotes")}</h3>
                                    <div className="space-y-3">
                                        {supportTicketNotes?.length > 0 ? (
                                            supportTicketNotes?.map((note: any) => (
                                                <div key={note.id} className="bg-yellow-50 rounded-lg p-3">
                                                    <div className="flex justify-between text-sm text-gray-500">
                                                        <span>{`${note.userId.firstName} ${note.userId.lastName}`}</span>
                                                        <span>{new Date(note.createdAt).toLocaleString()}</span>
                                                    </div>
                                                    <p className="mt-1">{note.note}</p>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500">{t("ticketDetailsModal.noInternalNotes")}</p>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={internalNote}
                                            onChange={e => setInternalNote(e.target.value)}
                                            placeholder={t("ticketDetailsModal.addInternalNote")}
                                            className="flex-1 px-3 py-2 border rounded-lg"
                                        />
                                        <Button disabled={!internalNote.trim()} onClick={addNote}>
                                            {t("ticketDetailsModal.add")}
                                        </Button>
                                    </div>
                                </div>

                                {/* Tags */}
                                <div className="space-y-4">
                                    <h3 className="font-medium">{t("ticketDetailsModal.tags")}</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {ticket.tags.map((tag, index) => (
                                            <Badge key={index} variant="outline">
                                                {tag}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                {/* History */}
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="font-medium">{t("ticketDetailsModal.history")}</h3>
                                        <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}>
                                            {showHistory ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                    {showHistory && (
                                        <div className="space-y-3">
                                            {ticket.history.map((event, index) => (
                                                <div key={index} className="text-sm space-y-1">
                                                    <p className="text-gray-700">{event.action}</p>
                                                    <div className="flex justify-between text-gray-500">
                                                        <span>{event.by}</span>
                                                        <span>{new Date(event.timestamp).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Action Buttons */}
                                <div className="space-y-3 pt-6 border-t">
                                    {Number(ticket.status) !== TicketStatusesEnum.Resolved && (
                                        <Button className="w-full" onClick={handleResolveTicket}>
                                            <CheckCircle className="h-4 w-4 mr-2" />
                                            {t("ticketDetailsModal.markAsResolved")}
                                        </Button>
                                    )}
                                    {Number(ticket.status) !== TicketStatusesEnum.Escalate && 
                                     Number(ticket.status) !== TicketStatusesEnum.Resolved && 
                                     Number(ticket.status) !== TicketStatusesEnum.Archived && (
                                        <Button variant="outline" className="w-full" onClick={handleEscalateTicket}>
                                            <CornerUpRight className="h-4 w-4 mr-2" />
                                            {t("ticketDetailsModal.escalateTicket")}
                                        </Button>
                                    )}
                                    {Number(ticket.status) !== TicketStatusesEnum.Archived && (
                                        <Button variant="outline" className="w-full text-yellow-600" onClick={handleArchiveTicket}>
                                            <Archive className="h-4 w-4 mr-2" />
                                            {t("ticketDetailsModal.archiveTicket")}
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default TicketDetailsModal;
